import { canonicalUrl } from "@/config/canonical";
import { useSeoCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import { Button } from "@/design-system/Button/Button";
import Card from "@/design-system/Card/Card";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";
import {
  evoStateItems,
  getEvoStateIcon,
} from "@/pages/EvolutionsHub/EvolutionsOverviewPage/EvolutionsOverviewPage";
import { OVERVIEW_STATES } from "@repo/shared/evolutions";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { EVOLUTION_STATES_GUIDE_PATH } from "@repo/shared/utils";
import {
  ArrowRight,
  CalendarCheck,
  Dna,
  LayoutGrid,
  RefreshCw,
  SlidersHorizontal,
  Target,
  Users,
} from "lucide-react";
import { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router";

const TOOLS = [
  {
    key: "players",
    to: "/evolutions/players",
    icon: <Users className="size-5" />,
  },
  {
    key: "builder",
    to: "/evolutions/builder",
    icon: <Dna className="size-5" />,
  },
  {
    key: "overview",
    to: "/evolutions/overview",
    icon: <LayoutGrid className="size-5" />,
  },
] as const;

const BENEFITS = [
  { key: "suggestions", icon: <Target className="size-5" /> },
  { key: "planning", icon: <CalendarCheck className="size-5" /> },
  { key: "progress", icon: <RefreshCw className="size-5" /> },
  { key: "control", icon: <SlidersHorizontal className="size-5" /> },
] as const;

/** An indexable guide to what each evolution state means and does. */
export const EvolutionStatesPage = () => {
  const { t } = useTranslation("evolutions");
  const seo = useSeoCopy("evolutionStates");
  const goBack = useGoBack({ fallback: "/evolutions/overview" });
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-background">
      <HeroSection
        helmetTitle={seo.title}
        helmetDescription={seo.description}
        pageTitle={seo.pageTitle}
        subtitle={seo.subtitle}
        helmetCanonicalLink={canonicalUrl(EVOLUTION_STATES_GUIDE_PATH)}
        back={goBack}
        shouldBeInTheDocumentFlow
      />
      <article className="mx-auto flex w-full min-w-0 max-w-[720px] flex-col gap-y-10 px-4 pt-6 pb-12">
        <div className="flex flex-col gap-y-3">
          <p className="text-xl font-semibold text-foreground">
            <Trans
              ns="evolutions"
              i18nKey="statesGuide.lead"
              components={{
                highlight: <span className="text-primary-blue" />,
              }}
            />
          </p>
          <p className="text-sm text-foreground-muted">
            {t("statesGuide.detection")}
          </p>
        </div>

        <Section title={t("statesGuide.statesTitle")}>
          <div className="flex flex-col gap-y-2">
            {OVERVIEW_STATES.map((state) => {
              const item = evoStateItems.find((i) => i.state === state)!;
              const included = state === "available";
              return (
                <Card
                  key={state}
                  hoverable={false}
                  className="flex items-start gap-x-3 p-4"
                >
                  <span className="mt-0.5 size-5 shrink-0 text-foreground-muted">
                    {getEvoStateIcon(state, 20)}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-y-1">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                      <h3 className="font-semibold text-foreground">
                        {t(item.labelKey)}
                      </h3>
                      <span
                        className={cn(
                          "rounded-full px-2.5 py-0.5 text-xs font-medium",
                          included
                            ? "bg-emerald-900/50 text-upgrade"
                            : "bg-control text-foreground-muted",
                        )}
                      >
                        {included
                          ? t("statesGuide.included")
                          : t("statesGuide.excluded")}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-muted">
                      {t(`statesGuide.states.${state}` as const)}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        </Section>

        <Section title={t("statesGuide.benefitsTitle")}>
          <div className="grid grid-cols-1 gap-3 mobile:grid-cols-2">
            {BENEFITS.map(({ key, icon }) => (
              <Card
                key={key}
                hoverable={false}
                className="flex flex-col gap-y-2 p-4"
              >
                <span className="text-foreground-muted">{icon}</span>
                <h3 className="font-semibold text-foreground">
                  {t(`statesGuide.benefits.${key}Title` as const)}
                </h3>
                <p className="text-sm text-foreground-muted">
                  {t(`statesGuide.benefits.${key}` as const)}
                </p>
              </Card>
            ))}
          </div>
        </Section>

        <Section title={t("statesGuide.startTitle")}>
          <p className="text-sm text-foreground-body">
            {t("statesGuide.startBody")}
          </p>
          <div className="grid grid-cols-1 gap-3 mobile:grid-cols-3">
            {TOOLS.map(({ key, to, icon }) => (
              <NavLink key={key} to={to}>
                <Card className="flex h-full flex-col gap-y-2 p-4">
                  <span className="flex items-center justify-between text-foreground-muted">
                    {icon}
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                  <h3 className="font-semibold text-foreground">
                    {t(`statesGuide.links.${key}Title` as const)}
                  </h3>
                  <p className="text-sm text-foreground-muted">
                    {t(`statesGuide.links.${key}` as const)}
                  </p>
                </Card>
              </NavLink>
            ))}
          </div>
          <div className="pt-2">
            <Button
              variant="primary"
              size="medium"
              onClick={() => navigate("/club-import-tutorial")}
            >
              {t("statesGuide.ctaImport")}
            </Button>
          </div>
        </Section>
      </article>
    </main>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-y-3">
    <h2 className="text-xl font-semibold text-foreground">{title}</h2>
    {children}
  </section>
);
